import { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  deleteCompletedTask,
  pollTranscriptionStatus,
} from "../redux/transcription-summary";
import type { AppDispatch } from "../store";
import { showLoading, showSuccess } from "../utils/notification";

// Define types for transcription tasks
interface TranscriptionTask {
  taskId: string;
  taskType: "voiceToText" | "assessmentScribe";
  status: string;
  startTime: number;
  endTime?: number;
  result?: string;
  caseNumber?: string;
  metadata?: {
    callback?: (result: string) => void;
    caseId?: string;
    parentTaskId?: string;
    notes?: string;
  };
}

interface TranscriptionState {
  activeTasks: Record<string, TranscriptionTask>;
  completedTasks: Record<string, TranscriptionTask>;
}

interface RootState {
  transcription: TranscriptionState;
}

export const TranscriptionStatusNotifier = () => {
  const dispatch = useDispatch<AppDispatch>();

  const activeTasks = useSelector(
    (state: RootState) => state.transcription.activeTasks
  );
  const completedTasks = useSelector(
    (state: RootState) => state.transcription.completedTasks
  );

  const activeTaskCount = Object.keys(activeTasks).length;
  const recentCompletedTask = Object.values(completedTasks).sort(
    (a: TranscriptionTask, b: TranscriptionTask) =>
      (b.endTime || 0) - (a.endTime || 0)
  )[0];

  // Resume polling for each active task on mount and show loading state
  useEffect(() => {
    const timeout = setTimeout(() => {
      const pendingTasks = Object.values(activeTasks).filter(
        (task: TranscriptionTask) =>
          task.status !== "SUCCESS" && task.status !== "FAILURE"
      );

      if (pendingTasks.length > 0) {
        // Show loading toast for pending tasks
        const caseNumbers = Array.from(
          new Set(pendingTasks.map((task) => task.caseNumber).filter(Boolean))
        );
        const caseInfo =
          caseNumbers.length > 0
            ? ` for Case${caseNumbers.length > 1 ? "s" : ""} ${caseNumbers.join(
                ", "
              )}`
            : "";
        showLoading(`Transcribing assessment${caseInfo}...`, dispatch);

        // Poll for each pending task. Anchor the elapsed cap (MDRS-223) to the
        // task's creation time, not now — otherwise every remount/re-fire of
        // this effect would hand the poll a fresh 20-min budget and a hung task
        // could be polled forever while the tab stays open.
        pendingTasks.forEach((task) => {
          dispatch(
            pollTranscriptionStatus({
              taskId: task.taskId,
              caseNumber: task.caseNumber || null,
              notes: task.metadata?.notes || null,
              startedAt: task.startTime,
            })
          );
        });
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [dispatch, activeTasks]);

  // Show success toast for recently completed task
  useEffect(() => {
    if (
      recentCompletedTask &&
      "endTime" in recentCompletedTask &&
      recentCompletedTask.endTime &&
      recentCompletedTask.endTime > Date.now() - 5000
    ) {
      const caseInfo =
        "caseNumber" in recentCompletedTask && recentCompletedTask.caseNumber
          ? ` for Case ${recentCompletedTask.caseNumber}`
          : "";

      if (
        "taskType" in recentCompletedTask &&
        recentCompletedTask.taskType === "voiceToText"
      ) {
        showSuccess(`Transcription complete${caseInfo}!`, dispatch);
      } else {
        showSuccess(`Transcription & Summarization complete${caseInfo}.`, dispatch);
      }
    }
  }, [recentCompletedTask, dispatch]);

  // Show loading toast while tasks are active
  useEffect(() => {
    if (activeTaskCount > 0) {
      const currentActiveTasks = Object.values(activeTasks);
      const newTasks = currentActiveTasks.filter(
        (task: TranscriptionTask) =>
          task.status !== "SUCCESS" && task.status !== "FAILURE"
      );
      currentActiveTasks.forEach((task: TranscriptionTask) => {
        if (task.status === "SUCCESS" || task.status === "FAILURE") {
          dispatch(deleteCompletedTask({ taskId: task.taskId }));
        }
      });

      if (newTasks.length === 0) {
        return;
      }

      const caseNumbers = Array.from(
        new Set(
          newTasks.map((task: TranscriptionTask) => task.caseNumber).filter(Boolean)
        )
      );
      const caseInfo =
        caseNumbers.length > 0
          ? ` for Case${caseNumbers.length > 1 ? "s" : ""} ${caseNumbers.join(", ")}`
          : "";

      showLoading(`Transcribing assessment${caseInfo}`, dispatch);
    }
  }, [activeTaskCount, activeTasks, dispatch]);

  return null;
};
